import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Badge, Box, Button, CircularProgress, IconButton, Snackbar, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { ExecutionOverview } from '../../../types/execution';
import executionService from '../../../services/executionService';
import ExecutiveView from './ExecutiveView';
import ScheduleView from './ScheduleView';
import RisksView from './RisksView';
import AccessView from './AccessView';
import ReportsView from './ReportsView';
import TasksView from './TasksView';
import ImportDialog from './ImportDialog';
import { errorMessage, toISODate } from './executionUtils';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface ExecutionPanelProps {
    projectId: number;
}

/** Aba "Execução" do projeto ativo: cronograma, riscos, acessos e relatório de seguimento. */
const ExecutionPanel: React.FC<ExecutionPanelProps> = ({ projectId }) => {
    const [referenceDate, setReferenceDate] = useState(toISODate(new Date()));
    const [overview, setOverview] = useState<ExecutionOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [importOpen, setImportOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: Severity }>({
        open: false, message: '', severity: 'success',
    });

    const notify = useCallback((message: string, severity: Severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setOverview(await executionService.getOverview(projectId, referenceDate));
        } catch (error) {
            notify(errorMessage(error, 'Erro ao carregar dados de execução'), 'error');
        } finally {
            setLoading(false);
        }
    }, [projectId, referenceDate, notify]);

    useEffect(() => { load(); }, [load]);

    const openRisks = overview ? [...overview.risks, ...overview.issues].filter((r) => r.status === 'OPEN').length : 0;

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                <TextField
                    type="date"
                    size="small"
                    label="Data de referência"
                    InputLabelProps={{ shrink: true }}
                    value={referenceDate}
                    onChange={(e) => e.target.value && setReferenceDate(e.target.value)}
                    helperText="Base do % esperado e dos lançamentos semanais"
                />
                <Button size="small" onClick={() => setReferenceDate(toISODate(new Date()))}>Hoje</Button>
                <Tooltip title="Recarregar">
                    <IconButton onClick={load}><RefreshIcon /></IconButton>
                </Tooltip>
                {loading && <CircularProgress size={20} />}
                <Box flexGrow={1} />
                <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                    Importar planilha
                </Button>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
                    <Tab label="Visão executiva" />
                    <Tab label="Cronograma" />
                    <Tab label="Tarefas" />
                    <Tab aria-label={`Riscos e incidências (${openRisks} abertos)`}
                        label={<Badge color="error" badgeContent={openRisks}>Riscos e incidências&nbsp;&nbsp;</Badge>} />
                    <Tab aria-label={`Equipe e acessos (${overview?.access.pending_count || 0} pendentes)`}
                        label={<Badge color="warning" badgeContent={overview?.access.pending_count || 0}>Equipe e acessos&nbsp;&nbsp;</Badge>} />
                    <Tab label="Relatórios" />
                </Tabs>
            </Box>

            {!overview ? (
                !loading && <Typography color="text.secondary">Não foi possível carregar os dados.</Typography>
            ) : (
                <>
                    {tab === 0 && <ExecutiveView overview={overview} />}
                    {tab === 1 && <ScheduleView projectId={projectId} overview={overview} reload={load} notify={notify} />}
                    {tab === 2 && <TasksView projectId={projectId} overview={overview} notify={notify} />}
                    {tab === 3 && <RisksView projectId={projectId} overview={overview} reload={load} notify={notify} />}
                    {tab === 4 && <AccessView projectId={projectId} overview={overview} reload={load} notify={notify} />}
                    {tab === 5 && <ReportsView projectId={projectId} overview={overview} notify={notify} />}
                </>
            )}

            <ImportDialog open={importOpen} projectId={projectId} onClose={() => setImportOpen(false)}
                onImported={() => { load(); notify('Planilha importada', 'success'); }} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ExecutionPanel;
